import { DateTimeFormatOptions } from 'next-intl';
import { Duration, Settings } from 'luxon';
import { isValidLocale } from '../locale/locale';

export const dateTimeFormatter = (
  date: Date,
  locale: string,
  options?: DateTimeFormatOptions,
  timezone?: boolean,
) => {
  const formatOptions: DateTimeFormatOptions = { ...options };

  if (timezone) {
    formatOptions.timeZone = Settings.defaultZone.name;
  }

  return Intl.DateTimeFormat(locale, formatOptions).format(date);
};

export const boundaryOfWeek = (date: Date, boundary: 'start' | 'end') => {
  const limit = boundary === 'start' ? 1 : 7;
  const diff =
    date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : limit);
  const boudaryDate = new Date(date);
  boudaryDate.setDate(diff);
  return boudaryDate;
};

export const smoothWeekDisplay = (
  startDate: Date,
  endDate: Date,
  locale: string,
  timezone?: boolean,
) => {
  const isOverTwoMonths = startDate.getMonth() !== endDate.getMonth();
  const isOverTwoYear = startDate.getFullYear() !== endDate.getFullYear();

  const weekDisplay = (day: Date, option: DateTimeFormatOptions) => {
    return `${dateTimeFormatter(day, locale, option, timezone)}`;
  };

  if (isOverTwoYear) {
    return `${weekDisplay(startDate, {
      month: 'short',
    })} ${weekDisplay(startDate, {
      year: 'numeric',
    })} – ${weekDisplay(endDate, {
      month: 'short',
    })} ${weekDisplay(endDate, {
      year: 'numeric',
    })}`;
  }
  if (isOverTwoMonths) {
    return `${weekDisplay(startDate, {
      month: 'short',
    })} – ${weekDisplay(endDate, {
      month: 'short',
    })} ${weekDisplay(endDate, {
      year: 'numeric',
    })}`;
  }
  return weekDisplay(startDate, {
    month: 'long',
    year: 'numeric',
  });
};

// Does this locale display time in 12-hour format by default?
export const hour12 = (locale: string) => {
  const localeTimeFormat = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
  });

  const resolvedOptions = localeTimeFormat.resolvedOptions();

  return resolvedOptions.hour12;
};

export const getDatePickerFormatForLocale = (
  localeBasename: Intl.Locale['baseName'],
) => {
  // Get the formatted parts for a sample date
  const parts = new Intl.DateTimeFormat(localeBasename).formatToParts(
    new Date(1999, 1, 1),
  );

  // Map the date parts to their positions
  const formatOrder = parts
    .filter(
      (
        part,
      ): part is Intl.DateTimeFormatPart & { type: 'day' | 'month' | 'year' } =>
        ['day', 'month', 'year'].includes(part.type),
    )
    .map((part) => part.type);

  // Generate the format string
  const formatMap = {
    day: 'dd',
    month: 'MM',
    year: 'yyyy',
  };

  return formatOrder.map((part) => formatMap[part]).join('/');
};

export const getWeekdayNameFromNumber = (
  dayNumber: number,
  locale: Intl.LocalesArgument = 'en-US',
  weekdayFormat: Intl.DateTimeFormatOptions['weekday'] = 'long',
) =>
  new Intl.DateTimeFormat(locale, { weekday: weekdayFormat }).format(
    new Date(2024, 0, dayNumber), // First day of 2024 is a Monday...
  );

export const formatDuration = (seconds?: number) => {
  if (!seconds) return undefined;

  const duration = Duration.fromObject({ seconds }).shiftTo('hours', 'minutes');

  let durationFormatted = '';
  if (duration?.hours > 0) {
    durationFormatted += `${Math.round(duration?.hours)}h`;

    if (duration?.minutes > 0) {
      durationFormatted += `${Math.round(duration?.minutes)}`;
    }
  } else {
    durationFormatted += `${Math.round(duration?.minutes)} min`;
  }

  return durationFormatted;
};

export const formatTimeFromNow = (locale: string, startSec: number) => {
  let validLocale = 'en';

  if (isValidLocale(locale)) {
    validLocale = locale;
  }

  const today = new Date();
  const nextSlot = new Date(startSec * 1000);
  const nextSlotDay = new Date(startSec * 1000);

  today.setHours(0, 0, 0, 0);
  nextSlotDay.setHours(0, 0, 0, 0);

  const diffInDays =
    (nextSlotDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

  if (diffInDays > 1) {
    // More than tomorrow - only displays the day
    return dateTimeFormatter(nextSlot, validLocale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // today or tomorrow - display relative time, hour and minute
  const nextSlotHour = dateTimeFormatter(nextSlot, validLocale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  // "today" / "tomorrow" labels
  const rtf = new Intl.RelativeTimeFormat(validLocale, { numeric: 'auto' });
  const relativeDay = rtf.format(diffInDays, 'day');

  return `${relativeDay}, ${nextSlotHour}`;
};
